import { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Link as LinkIcon, X, Loader2, Image as ImageIcon, Check, RotateCcw, Crop as CropIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const MAX_UPLOAD_MB = 5;
const RESIZE_MAX_WIDTH = 1600; // px

function resolvePreview(value) {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:")) return value;
  if (value.startsWith("/api/uploads/")) return `${BACKEND_URL}${value}`;
  return value;
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}

/**
 * Load a data URL into a HTMLImageElement (returns a Promise).
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = src;
  });
}

/**
 * Given the source image and a pixel-crop region, return an object with
 * { dataUrl, contentType }. Also downscales so the output width <= RESIZE_MAX_WIDTH.
 */
async function cropAndResize(srcDataUrl, pixelCrop, sourceType) {
  const img = await loadImage(srcDataUrl);
  const region = pixelCrop || { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };
  // Downscale the crop if wider than the max
  const scale = region.width > RESIZE_MAX_WIDTH ? RESIZE_MAX_WIDTH / region.width : 1;
  const outW = Math.max(1, Math.round(region.width * scale));
  const outH = Math.max(1, Math.round(region.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(img, region.x, region.y, region.width, region.height, 0, 0, outW, outH);
  const outputType = sourceType === "image/png" ? "image/png" : "image/jpeg";
  const quality = outputType === "image/jpeg" ? 0.86 : undefined;
  const dataUrl = canvas.toDataURL(outputType, quality);
  return { dataUrl, contentType: outputType, width: outW, height: outH };
}

/**
 * ImageInput
 *
 * Props:
 * - value: string (URL or /api/uploads/ path)
 * - onChange: (newValue) => void
 * - label: display label
 * - helper: optional helper text
 * - aspect: optional crop aspect ratio (e.g. 16/9). If provided, uploaded files
 *   are cropped to this aspect before upload. URL-pasted images are used as-is.
 * - testId: base testid prefix
 */
export default function ImageInput({ value, onChange, label = "Image", helper, aspect, testId }) {
  const initialMode = value && value.startsWith("/api/uploads/") ? "upload" : "url";
  const [mode, setMode] = useState(initialMode);
  const [urlDraft, setUrlDraft] = useState(value || "");
  const [preview, setPreview] = useState(resolvePreview(value));

  // Cropper state
  const [rawImage, setRawImage] = useState(null); // { dataUrl, contentType, filename }
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelCrop, setPixelCrop] = useState(null);
  const [uploading, setUploading] = useState(false);

  const inputRef = useRef(null);

  useEffect(() => {
    setUrlDraft(value || "");
    setPreview(resolvePreview(value));
    if (value && value.startsWith("/api/uploads/")) setMode("upload");
  }, [value]);

  const applyUrl = () => {
    const clean = (urlDraft || "").trim();
    onChange(clean);
    setPreview(resolvePreview(clean));
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("That file isn't an image.");
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024 * 3) {
      toast.error(`Original file is too large. Please pick something under ${MAX_UPLOAD_MB * 3} MB.`);
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setRawImage({ dataUrl, contentType: file.type, filename: file.name });
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setPixelCrop(null);
    } catch (e) {
      toast.error(e?.message || "Could not read file");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (uploading) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const onCropComplete = useCallback((_area, areaPixels) => {
    setPixelCrop(areaPixels);
  }, []);

  const cancelCrop = () => {
    setRawImage(null);
    setPixelCrop(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const doUpload = async (useCrop) => {
    if (!rawImage) return;
    setUploading(true);
    try {
      const { dataUrl, contentType } = await cropAndResize(
        rawImage.dataUrl,
        useCrop ? pixelCrop : null,
        rawImage.contentType,
      );
      const base64 = dataUrl.split(",")[1];
      const sizeBytes = Math.floor(base64.length * 3 / 4);
      if (sizeBytes > MAX_UPLOAD_MB * 1024 * 1024) {
        throw new Error(`Processed image still exceeds ${MAX_UPLOAD_MB} MB.`);
      }
      const r = await api.post("/admin/uploads", {
        filename: rawImage.filename,
        content_type: contentType,
        data_base64: base64,
      });
      const url = r.data?.url;
      if (!url) throw new Error("Upload response missing url");
      onChange(url);
      setPreview(resolvePreview(url));
      cancelCrop();
      toast.success(`Uploaded — ${(r.data.size_bytes / 1024).toFixed(0)} KB stored.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    onChange("");
    setUrlDraft("");
    setPreview("");
    cancelCrop();
  };

  return (
    <div data-testid={testId || "image-input"}>
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">{label}</label>
        {value && (
          <button type="button" onClick={clear} className="text-xs text-[#94A3B8] hover:text-[#B42318] transition-colors inline-flex items-center gap-1">
            <X className="h-3 w-3" aria-hidden /> Clear
          </button>
        )}
      </div>

      {/* Mode tabs */}
      <div className="mt-2 inline-flex rounded-xl border border-[#26334D] bg-[#1A2232] p-1">
        <button
          type="button"
          data-testid={`${testId || "image-input"}-mode-url`}
          onClick={() => { setMode("url"); cancelCrop(); }}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${mode === "url" ? "bg-[#E85A2A] text-[#F8FAFC]" : "text-[#94A3B8] hover:text-[#F8FAFC]"}`}
        >
          <LinkIcon className="h-3.5 w-3.5" aria-hidden />
          Paste URL
        </button>
        <button
          type="button"
          data-testid={`${testId || "image-input"}-mode-upload`}
          onClick={() => setMode("upload")}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${mode === "upload" ? "bg-[#E85A2A] text-[#F8FAFC]" : "text-[#94A3B8] hover:text-[#F8FAFC]"}`}
        >
          <UploadCloud className="h-3.5 w-3.5" aria-hidden />
          Upload from computer
        </button>
      </div>

      {/* URL MODE */}
      {mode === "url" && (
        <div className="mt-3 flex items-stretch gap-2">
          <input
            type="url"
            inputMode="url"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onBlur={applyUrl}
            placeholder="https://images.unsplash.com/…"
            data-testid={`${testId || "image-input"}-url-field`}
            className="h-11 flex-1 rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
          />
          <button
            type="button"
            onClick={applyUrl}
            className="rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45]"
          >Use URL</button>
        </div>
      )}

      {/* UPLOAD MODE — drop zone OR cropper */}
      {mode === "upload" && !rawImage && (
        <div
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={onDrop}
          className="mt-3 rounded-2xl border-2 border-dashed border-[#26334D] bg-[#111622] p-5 text-center"
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            data-testid={`${testId || "image-input"}-file`}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <UploadCloud className="mx-auto h-6 w-6 text-[#94A3B8]" aria-hidden />
          <p className="mt-2 text-sm text-[#F8FAFC]" style={{ lineHeight: 1.6 }}>
            Drag an image here, or
          </p>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId || "image-input"}-pick`}
            className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2 text-sm font-semibold hover:bg-[#C94A1D]"
          >
            <ImageIcon className="h-4 w-4" />
            Choose file
          </button>
          <p className="mt-3 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
            JPG · PNG · WEBP · GIF · up to {MAX_UPLOAD_MB} MB · resized to {RESIZE_MAX_WIDTH}px wide automatically
            {aspect ? " · cropped to your framing" : ""}
          </p>
        </div>
      )}

      {/* CROP UI (only when a raw file is loaded and awaiting user framing) */}
      {mode === "upload" && rawImage && (
        <div className="mt-3 rounded-2xl border border-[#26334D] bg-[#1A2232] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#26334D] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CropIcon className="h-4 w-4 text-[#E85A2A]" aria-hidden />
              <span className="text-sm font-semibold text-[#F8FAFC]">
                {aspect ? `Frame your image (${aspect === 16/9 ? "16:9" : `${aspect.toFixed(2)}:1`})` : "Preview your image"}
              </span>
            </div>
            <button
              type="button"
              onClick={cancelCrop}
              className="text-xs text-[#94A3B8] hover:text-[#B42318] inline-flex items-center gap-1"
              aria-label="Cancel"
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          </div>
          <div className="relative w-full bg-black" style={{ height: 360 }}>
            <Cropper
              image={rawImage.dataUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect || undefined}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit={aspect ? "cover" : "contain"}
              showGrid={!!aspect}
              cropShape="rect"
              style={{
                containerStyle: { backgroundColor: "#0B1220" },
              }}
            />
          </div>
          <div className="px-4 py-3 border-t border-[#26334D] space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase w-14 shrink-0">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-[#E85A2A]"
                aria-label="Zoom"
              />
              <span className="text-xs text-[#94A3B8] tabular-nums w-10 text-right">{zoom.toFixed(2)}x</span>
            </div>
            <p className="text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
              Drag the image to reposition. This is your focal point — whatever sits in the frame will be what students see.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => doUpload(false)}
                disabled={uploading}
                className="rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2.5 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                <RotateCcw className="h-4 w-4" aria-hidden />
                Use full image (no crop)
              </button>
              <button
                type="button"
                onClick={() => doUpload(true)}
                disabled={uploading || !pixelCrop}
                data-testid={`${testId || "image-input"}-apply-crop`}
                className="flex-1 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2.5 text-sm font-semibold hover:bg-[#C94A1D] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {uploading ? "Uploading…" : (aspect ? "Apply crop & upload" : "Upload cropped area")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live preview of the SAVED value (post-crop) */}
      {preview && !rawImage && (
        <div className="mt-3 overflow-hidden rounded-xl border border-[#26334D] bg-[#1A2232]">
          <img
            src={preview}
            alt="Preview"
            className="w-full max-h-64 object-contain bg-[#111622]"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}

      {helper && (
        <p className="mt-2 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>{helper}</p>
      )}
    </div>
  );
}
